// React import not needed with new JSX transform
import { SceneObject, GeometricProperties } from '../db/service'

interface InspectorProps {
  selectedObject: SceneObject | null
  onPropertyChange: (objectId: string, properties: GeometricProperties) => void
}

export function Inspector({ selectedObject, onPropertyChange }: InspectorProps) {
  if (!selectedObject) {
    return (
      <div className="inspector">
        <div className="inspector-header">
          <h3>Inspector</h3>
        </div>
        <div className="inspector-content">
          <p className="inspector-placeholder">Select an object to edit its properties</p>
        </div>
      </div>
    )
  }

  const properties = selectedObject.properties || {}

  const handlePropertyChange = (key: keyof GeometricProperties, value: number) => {
    const newProperties = {
      ...properties,
      [key]: value
    }
    onPropertyChange(selectedObject.id, newProperties)
  }

  const renderPropertyControls = () => {
    switch (selectedObject.type) {
      case 'sphere':
        return (
          <>
            <div className="property-group">
              <label>Segments</label>
              <input
                type="range"
                min="8"
                max="64"
                value={properties.sphereSegments || 16}
                onChange={(e) => handlePropertyChange('sphereSegments', parseInt(e.target.value))}
              />
              <span className="property-value">{properties.sphereSegments || 16}</span>
            </div>
            <div className="property-group">
              <label>Rings</label>
              <input
                type="range"
                min="4"
                max="32"
                value={properties.sphereRings || 16}
                onChange={(e) => handlePropertyChange('sphereRings', parseInt(e.target.value))}
              />
              <span className="property-value">{properties.sphereRings || 16}</span>
            </div>
          </>
        )

      case 'cylinder':
        return (
          <>
            <div className="property-group">
              <label>Radial Segments</label>
              <input
                type="range"
                min="8"
                max="64"
                value={properties.cylinderRadialSegments || 16}
                onChange={(e) => handlePropertyChange('cylinderRadialSegments', parseInt(e.target.value))}
              />
              <span className="property-value">{properties.cylinderRadialSegments || 16}</span>
            </div>
            <div className="property-group">
              <label>Height Segments</label>
              <input
                type="range"
                min="1"
                max="16"
                value={properties.cylinderHeightSegments || 1}
                onChange={(e) => handlePropertyChange('cylinderHeightSegments', parseInt(e.target.value))}
              />
              <span className="property-value">{properties.cylinderHeightSegments || 1}</span>
            </div>
          </>
        )

      case 'cone':
        return (
          <>
            <div className="property-group">
              <label>Radial Segments</label>
              <input
                type="range"
                min="8"
                max="64"
                value={properties.coneRadialSegments || 8}
                onChange={(e) => handlePropertyChange('coneRadialSegments', parseInt(e.target.value))}
              />
              <span className="property-value">{properties.coneRadialSegments || 8}</span>
            </div>
            <div className="property-group">
              <label>Height Segments</label>
              <input
                type="range"
                min="1"
                max="16"
                value={properties.coneHeightSegments || 1}
                onChange={(e) => handlePropertyChange('coneHeightSegments', parseInt(e.target.value))}
              />
              <span className="property-value">{properties.coneHeightSegments || 1}</span>
            </div>
          </>
        )

      case 'cube':
        return (
          <>
            <div className="property-group">
              <label>Width Segments</label>
              <input
                type="range"
                min="1"
                max="16"
                value={properties.cubeWidthSegments || 1}
                onChange={(e) => handlePropertyChange('cubeWidthSegments', parseInt(e.target.value))}
              />
              <span className="property-value">{properties.cubeWidthSegments || 1}</span>
            </div>
            <div className="property-group">
              <label>Height Segments</label>
              <input
                type="range"
                min="1"
                max="16"
                value={properties.cubeHeightSegments || 1}
                onChange={(e) => handlePropertyChange('cubeHeightSegments', parseInt(e.target.value))}
              />
              <span className="property-value">{properties.cubeHeightSegments || 1}</span>
            </div>
            <div className="property-group">
              <label>Depth Segments</label>
              <input
                type="range"
                min="1"
                max="16"
                value={properties.cubeDepthSegments || 1}
                onChange={(e) => handlePropertyChange('cubeDepthSegments', parseInt(e.target.value))}
              />
              <span className="property-value">{properties.cubeDepthSegments || 1}</span>
            </div>
          </>
        )

      case 'plane':
        return (
          <>
            <div className="property-group">
              <label>Width Segments</label>
              <input
                type="range"
                min="1"
                max="32"
                value={properties.planeWidthSegments || 1}
                onChange={(e) => handlePropertyChange('planeWidthSegments', parseInt(e.target.value))}
              />
              <span className="property-value">{properties.planeWidthSegments || 1}</span>
            </div>
            <div className="property-group">
              <label>Height Segments</label>
              <input
                type="range"
                min="1"
                max="32"
                value={properties.planeHeightSegments || 1}
                onChange={(e) => handlePropertyChange('planeHeightSegments', parseInt(e.target.value))}
              />
              <span className="property-value">{properties.planeHeightSegments || 1}</span>
            </div>
          </>
        )

      default:
        return <p>No properties available for this object type</p>
    }
  }

  return (
    <div className="inspector">
      <div className="inspector-header">
        <h3>Inspector</h3>
        <div className="object-info">
          <span className="object-name">{selectedObject.name || `${selectedObject.type}-${selectedObject.id.slice(-6)}`}</span>
          <span className="object-type">{selectedObject.type}</span>
        </div>
      </div>
      <div className="inspector-content">
        <div className="property-section">
          <h4>Geometry</h4>
          {renderPropertyControls()}
        </div>
      </div>
    </div>
  )
} 